import React from "react";

import { useDeleteTask, useGetDag } from "api/api-hooks/airflow-hooks";
import ActionButtons from "components/basic/action-buttons/ActionButtons";
import CollapsibleText from "components/basic/collapsible-text/CollapsibleText";
import DataTable from "components/basic/data-table/DataTable";
import UserAvatar from "components/basic/user-avatar/UserAvatar";
import { useHasPermissions } from "hooks/useHasPermissions";
import { Card } from "primereact/card";
import { DataTableProps } from "primereact/datatable";
import { Tag } from "primereact/tag";
import { Link, useParams } from "react-router-dom";
import { T } from "services/lang/LanguageService";
import { AirflowTaskDto } from "types/airflow";
import { DEFAULT_DATE_FORMATTER } from "utils/constants";
import { parseToLocalZoneDateTime } from "utils/time-utils";
import { TableColumn } from "../measurement-devices/utils/types";
import DagHeader from "./components/DagHeader";

const AirflowTasks: React.FC = () => {
  const { dagId } = useParams();
  const { data, isLoading } = useGetDag(dagId ?? "");
  const deleteTask = useDeleteTask();

  const canEdit = useHasPermissions("AIRFLOW_EDIT");

  if (!dagId) return null;

  const renderActions = (rowData: AirflowTaskDto) => {
    return (
      <ActionButtons
        onClickDelete={() => deleteTask.mutate({ dagId, taskId: rowData.id.taskId })}
      />
    );
  };

  const renderTaskTags = (streams: string[], prefix: "up" | "down") => (
    <div className="flex flex-column gap-1">
      {streams.length === 0 && <span>{`No ${prefix}stream tasks`}</span>}
      {streams.map(id => (
        <Tag key={id} value={id} />
      ))}
    </div>
  );

  const renderLink = (rowData: AirflowTaskDto) => (
    <Link to={rowData.id.taskId}>{rowData.title}</Link>
  );

  const renderTaskId = (rowData: AirflowTaskDto) => <CollapsibleText text={rowData.id.taskId} />;

  const renderUserAvatar = (rowData: AirflowTaskDto) =>
    rowData.lastUpdatedBy && <UserAvatar username={rowData.lastUpdatedBy} />;

  const renderStatus = (rowData: AirflowTaskDto) => (
    <div>
      {rowData.isSkipped && (
        <i className="pi pi-power-off text-red-600" title="Task is skipped (disabled)" />
      )}
      {rowData.showStepWarning && (
        <i
          className="pi pi-exclamation-triangle text-orange-600"
          title="Task is executing with more than 1 period steps!"
        />
      )}
    </div>
  );

  const columns: TableColumn<AirflowTaskDto>[] = [
    {
      field: "id.taskId",
      header: T("word.task-id"),
      columnKey: "taskId",
      body: renderTaskId,
      sortable: true,
    },
    {
      field: "title",
      header: T("word.title"),
      body: renderLink,
      columnKey: "title",
      sortable: true,
    },
    {
      field: "description",
      header: T("word.description"),
      style: { maxWidth: "30rem", wordBreak: "break-word" },
      columnKey: "description",
    },
    {
      field: "upstreamTasks",
      header: T("word.invoked-by"),
      columnKey: "upstreamTasks",
      body: rowData => renderTaskTags(rowData.upstreamTasks, "up"),
    },
    {
      field: "downstreamTasks",
      header: T("word.invokes"),
      columnKey: "downstreamTasks",
      body: rowData => renderTaskTags(rowData.downstreamTasks, "down"),
    },
    {
      field: "lastUpdated",
      header: T("word.updated-at"),
      body: rowData =>
        rowData.lastUpdated &&
        parseToLocalZoneDateTime(rowData.lastUpdated).format(DEFAULT_DATE_FORMATTER),
      columnKey: "lastUpdated",
      sortable: true,
    },
    {
      field: "lastUpdatedBy",
      header: T("word.updated-by"),
      body: renderUserAvatar,
      columnKey: "lastUpdatedBy",
      sortable: true,
    },
    {
      header: T("word.status"),
      columnKey: "status",
      body: renderStatus,
      style: { justifyItems: "center" },
    },
    {
      header: T("word.actions"),
      columnKey: "actions",
      body: renderActions,
      style: { justifyItems: "center" },
    },
  ];

  const tableConfig: DataTableProps<AirflowTaskDto[]> = {
    loading: isLoading,
    header: canEdit && <DagHeader dag={data!} />,
    rows: 25,
  };

  return (
    <Card title={`Airflow DAG: ${data?.title}`}>
      <DataTable
        data={data?.tasks ?? []}
        tableConfig={tableConfig}
        columns={columns.filter(col => (col.columnKey === "actions" ? canEdit : true))}
      />
    </Card>
  );
};

export default AirflowTasks;
